// Cantera.jsx

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import {
  Box,
  Grid,
  Typography,
  Avatar,
  Tab,
  Tabs,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Healing as HealingIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Lightbulb as LightbulbIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoLibraryIcon,
  LocalHospital as LocalHospitalIcon,
  Straighten as StraightenIcon,
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Save as SaveIcon,
  TrendingUp,
  TrendingFlat,
  TrendingDown,
} from '@mui/icons-material';

import Estadisticas from './Estadisticas'; // eslint-disable-line no-unused-vars
import EstadisticasNew from './EstadisticasNew';
import GameCenterEstadisticas from './GameCenterEstadisticas';
import GameCenterVideo from './GameCenterVideo';
import FlagDisplay from '../components/FlagDisplay';

import { useStatsTheme } from './v2/theme/statsTheme';
import {
  CanteraCard,
  CanteraButton,
  CanteraTextField,
} from './components/shared';

// v2 stats UI is now the default. Escape hatch via localStorage to fall back
// to the legacy EstadisticasNew screen during the rollout window:
//   localStorage.setItem('canteraStatsLegacy', 'true')  → fall back to v1
//   localStorage.removeItem('canteraStatsLegacy')       → use v2 (default)
import { StatsRouter as StatsRouterV2 } from './v2/screens';
const isLegacyStatsUIEnabled = () => {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('canteraStatsLegacy') === 'true';
  } catch (_) {
    return false;
  }
};

const Cantera = () => {
  const { t } = useTranslation();
  const theme = useStatsTheme();
  const { groupeId } = useParams();

  const [players, setPlayers] = useState([]);
  const [group, setGroup] = useState(null);
  const [teamAverage, setTeamAverage] = useState({});
  const [teamPrevious, setTeamPrevious] = useState({});
  const [teamCurrentWeek, setTeamCurrentWeek] = useState({});
  const [teamPreviousWeek, setTeamPreviousWeek] = useState({});
  const [selectedDate, setSelectedDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState({});
  const [playerEmails, setPlayerEmails] = useState({});
  const [emailLoading, setEmailLoading] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [currentTab, setCurrentTab] = useState('plantilla');
  const [showHeader, setShowHeader] = useState(true);

  const mediaUrl = getMediaBaseUrl();

  const getTeamPhotoUrl = (id) => `${mediaUrl}/cantera/${id}.jpg?${new Date().getTime()}`;
  const getPlayerAvatarUrl = (playerId) =>
    playerId ? `${mediaUrl}/photos/${playerId}.png?${new Date().getTime()}` : '/path/to/default-player-avatar.png';

  const fetchDailyInfos = async () => {
    try {
      setLoading(true);
      const [dailyResponse, playersResponse] = await Promise.all([
        api.get(`/api/cantera/${groupeId}/daily`),
        api.get(`/api/cantera/${groupeId}/players`),
      ]);

      const dailyData = dailyResponse.data;
      const playersData = playersResponse.data;

      setGroup(dailyData.group || playersData.group);
      setTeamAverage(dailyData.teamAverage || {});
      setTeamPrevious(dailyData.teamPrevious || {});
      setTeamCurrentWeek(dailyData.teamCurrentWeek || {});
      setTeamPreviousWeek(dailyData.teamPreviousWeek || {});
      setSelectedDate(dailyData.selectedDate || '');

      const dailyPlayersMap = new Map();
      if (dailyData.players) {
        dailyData.players.forEach((player) => dailyPlayersMap.set(player.player_id, player));
      }

      const mergedPlayers = (playersData.players || []).map((player) => {
        const dailyInfo = dailyPlayersMap.get(player.player_id) || {};
        return {
          ...player,
          ...dailyInfo,
          nom: player.nom || dailyInfo.nom,
          poste: player.poste || dailyInfo.poste,
          date_naissance: player.date_naissance || dailyInfo.date_naissance,
          taille: player.taille || dailyInfo.taille,
          poids: player.poids || dailyInfo.poids,
          nationalite: player.nationalite || dailyInfo.nationalite,
        };
      });

      setPlayers(mergedPlayers);

      const emailsMap = {};
      mergedPlayers.forEach((player) => {
        if (player.mail) emailsMap[player.player_id] = player.mail;
      });
      setPlayerEmails(emailsMap);
    } catch (error) {
      console.error('Erreur lors de la récupération des infos:', error);
      try {
        const response = await api.get(`/api/cantera/${groupeId}/players`);
        const { group: g, players: p } = response.data;
        setGroup(g);
        setPlayers(p || []);
      } catch (fallbackError) {
        console.error('Erreur lors de la récupération des infos de base:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (groupeId) fetchDailyInfos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupeId]);

  const getDecimalAge = (dateNaissance) => {
    if (!dateNaissance) return '';
    const now = new Date();
    const dob = new Date(dateNaissance);
    const diffMs = now - dob;
    if (diffMs < 0) return '';
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    const diffYears = diffDays / 365.25;
    return diffYears.toFixed(1);
  };

  const formatDate = (isoDateString) => {
    if (!isoDateString) return '';
    const parts = isoDateString.split('-');
    if (parts.length !== 3) return isoDateString;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const handleExpandClick = (playerId, event) => {
    event.preventDefault();
    event.stopPropagation();
    setExpandedCards((prev) => ({ ...prev, [playerId]: !prev[playerId] }));
  };

  const handleEmailChange = (playerId, email) => {
    setPlayerEmails((prev) => ({ ...prev, [playerId]: email }));
  };

  const handleSaveEmail = async (playerId) => {
    const email = playerEmails[playerId];
    if (!email) {
      setSnackbar({ open: true, message: t('canteraPage.messages.enterEmail'), severity: 'error' });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSnackbar({ open: true, message: t('canteraPage.messages.invalidEmail'), severity: 'error' });
      return;
    }
    setEmailLoading((prev) => ({ ...prev, [playerId]: true }));
    try {
      const response = await api.put(`/api/cantera/player/${playerId}/mail`, { mail: email });
      if (response.data.success) {
        setSnackbar({ open: true, message: t('canteraPage.messages.emailSaved'), severity: 'success' });
        fetchDailyInfos();
      }
    } catch (error) {
      console.error('Error al guardar el correo:', error);
      const errorMessage = error.response?.data?.error || t('canteraPage.messages.saveError');
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setEmailLoading((prev) => ({ ...prev, [playerId]: false }));
    }
  };

  const handleSendAccess = async (playerId) => {
    setEmailLoading((prev) => ({ ...prev, [playerId]: true }));
    try {
      const response = await api.post(`/api/cantera/player/${playerId}/send-access`, {});
      if (response.data.success) {
        setSnackbar({ open: true, message: t('canteraPage.messages.emailSent'), severity: 'success' });
      }
    } catch (error) {
      console.error('Error al enviar el correo:', error);
      const errorMessage = error.response?.data?.error || t('canteraPage.messages.sendError');
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setEmailLoading((prev) => ({ ...prev, [playerId]: false }));
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleTabChange = (event, newValue) => {
    if (window.estadisticasConfirmNavigation) {
      const canNavigate = window.estadisticasConfirmNavigation(() => {
        setCurrentTab(newValue);
        if (newValue === 'plantilla') fetchDailyInfos();
      });
      if (canNavigate) {
        setCurrentTab(newValue);
        if (newValue === 'plantilla') fetchDailyInfos();
      }
    } else {
      setCurrentTab(newValue);
      if (newValue === 'plantilla') fetchDailyInfos();
    }
  };

  const getTrendIcon = (current, previous, inverse = false) => {
    if (current === null || current === undefined || previous === null || previous === undefined) return null;
    const currentVal = parseFloat(current);
    const previousVal = parseFloat(previous);
    if (isNaN(currentVal) || isNaN(previousVal)) return null;
    const diff = currentVal - previousVal;
    const actualDiff = inverse ? -diff : diff;
    if (actualDiff > 0.5) return <TrendingUp sx={{ fontSize: 18, color: theme.actionColors.made, ml: 0.5 }} />;
    if (actualDiff < -0.5) return <TrendingDown sx={{ fontSize: 18, color: theme.actionColors.missed, ml: 0.5 }} />;
    return <TrendingFlat sx={{ fontSize: 18, color: theme.actionColors.warning, ml: 0.5 }} />;
  };

  // Catégories d'indicateurs → couleurs sémantiques
  const dailyMetrics = [
    { key: 'sleep', color: theme.actionColors.neutral, label: t('canteraPage.sleepEnergy'), shortLabel: t('canteraPage.sleep') },
    { key: 'pain', color: theme.actionColors.missed, label: t('canteraPage.painPhysical'), shortLabel: t('canteraPage.pain'), inverse: true },
    { key: 'mood', color: theme.actionColors.info, label: t('canteraPage.moodMental'), shortLabel: t('canteraPage.mood') },
    { key: 'nutrition', color: theme.actionColors.made, label: t('canteraPage.nutrition'), shortLabel: t('canteraPage.nutrition') },
  ];

  const renderTeamAverage = () => {
    const lastRecordDate = teamAverage.last_record_date
      ? formatDate(teamAverage.last_record_date)
      : formatDate(selectedDate);

    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          sx={{
            color: theme.text.primary,
            fontSize: { xs: 16, sm: 18 },
            fontWeight: 700,
            letterSpacing: '-0.01em',
            textAlign: 'center',
            mb: 2,
          }}
        >
          {t('canteraPage.averageStatus')} <Box component="span" sx={{ color: theme.text.muted, fontWeight: 500 }}>({lastRecordDate})</Box>
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: 1.5,
          }}
        >
          {dailyMetrics.map((m) => {
            const avg = teamAverage[`avg_${m.key}`];
            const prev = teamPrevious?.[`avg_${m.key}`];
            const wkCur = teamCurrentWeek?.[`avg_${m.key}`];
            const wkPrev = teamPreviousWeek?.[`avg_${m.key}`];
            return (
              <Box
                key={m.key}
                sx={{
                  bgcolor: theme.withAlpha(m.color, 0.12),
                  border: `1px solid ${theme.withAlpha(m.color, 0.3)}`,
                  borderRadius: `${theme.radius.lg}px`,
                  p: 1.5,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ color: theme.text.secondary, fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', mb: 0.5 }}>
                  {m.label}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                  <Typography sx={{ color: m.color, fontSize: 24, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                    {avg || 0}
                  </Typography>
                  <Typography sx={{ color: theme.text.muted, fontSize: 13, fontWeight: 500 }}>/ 10</Typography>
                  {getTrendIcon(avg, prev, m.inverse)}
                </Stack>
                <Typography sx={{ color: theme.text.muted, fontSize: 10, mt: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {t('canteraPage.week')}: {wkCur || '-'} {t('canteraPage.vs')} {wkPrev || '-'}
                  {getTrendIcon(wkCur, wkPrev, m.inverse)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  // Badges : chaque type d'activité a une couleur sémantique du thème
  const badgeConfigs = (counts) => [
    { show: counts.count_big_board > 0, icon: <StarIcon sx={{ fontSize: 14 }} />, label: t('canteraPage.ranking'), count: counts.count_big_board, color: theme.accent },
    { show: counts.count_follow_list > 0, icon: <VisibilityIcon sx={{ fontSize: 14 }} />, label: t('canteraPage.tracking'), count: counts.count_follow_list, color: theme.actionColors.ballHandling.stl },
    { show: counts.count_informe > 0, icon: <AssignmentIcon sx={{ fontSize: 14 }} />, label: t('canteraPage.report'), count: counts.count_informe, color: theme.actionColors.made },
    { show: counts.count_intel > 0, icon: <LightbulbIcon sx={{ fontSize: 14 }} />, label: t('canteraPage.intel'), count: counts.count_intel, color: theme.actionColors.warning },
    { show: counts.count_fiche_technique > 0, icon: <DescriptionIcon sx={{ fontSize: 14 }} />, label: t('canteraPage.technicalSheet'), count: counts.count_fiche_technique, color: theme.actionColors.info },
    { show: counts.count_video > 0, icon: <VideoLibraryIcon sx={{ fontSize: 14 }} />, label: t('canteraPage.video'), count: counts.count_video, color: theme.actionColors.ballHandling.tov },
    { show: counts.count_medical > 0, icon: <LocalHospitalIcon sx={{ fontSize: 14 }} />, label: t('canteraPage.medical'), count: counts.count_medical, color: theme.actionColors.missed },
    { show: counts.count_medidas > 0, icon: <StraightenIcon sx={{ fontSize: 14 }} />, label: t('canteraPage.measures'), count: counts.count_medidas, color: theme.actionColors.defense.blk },
  ];

  const renderPlayers = () => (
    <Grid container spacing={2}>
      {players.map((player) => {
        const {
          player_id, nom, poste, date_naissance, taille, nationalite,
          count_big_board, count_follow_list, count_informe, count_intel,
          count_fiche_technique, count_video, in_injury, count_medical, count_medidas,
          sleep, pain, mood, nutrition, pain_zone, comment,
          prev_sleep, prev_pain, prev_mood, prev_nutrition,
        } = player;

        const isExpanded = expandedCards[player_id] || false;
        const hasBadges = count_big_board > 0 || count_follow_list > 0 || count_informe > 0
          || count_intel > 0 || count_fiche_technique > 0 || count_video > 0
          || count_medical > 0 || count_medidas > 0;
        const metricsByKey = { sleep, pain, mood, nutrition };
        const prevByKey = { sleep: prev_sleep, pain: prev_pain, mood: prev_mood, nutrition: prev_nutrition };
        const hasDaily = dailyMetrics.some((m) => metricsByKey[m.key] !== undefined && metricsByKey[m.key] !== null);

        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={player_id}>
            <CanteraCard tone="dark" padding={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Link
                to={`/players/${player_id}`}
                style={{ textDecoration: 'none', color: 'inherit', flexGrow: 1, display: 'block' }}
              >
                <Box sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Avatar + nom */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
                    <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ position: 'relative' }}>
                        <Avatar
                          src={getPlayerAvatarUrl(player_id)}
                          alt={nom}
                          sx={{
                            width: 56, height: 56,
                            border: `2px solid ${theme.surfaces.border}`,
                          }}
                        />
                        {in_injury === 1 && (
                          <Box
                            sx={{
                              position: 'absolute', top: -4, right: -4,
                              bgcolor: theme.surfaces.floating,
                              borderRadius: '50%', p: 0.25,
                              border: `1px solid ${theme.withAlpha(theme.actionColors.missed, 0.4)}`,
                              display: 'flex',
                            }}
                          >
                            <HealingIcon sx={{ color: theme.actionColors.missed, fontSize: 16 }} />
                          </Box>
                        )}
                      </Box>
                      {nationalite && <FlagDisplay nationality={nationalite} size="20px" />}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: theme.text.primary,
                          fontSize: { xs: 15, md: 16 },
                          fontWeight: 700,
                          lineHeight: 1.2,
                          letterSpacing: '-0.01em',
                          wordBreak: 'break-word',
                          mb: 0.5,
                        }}
                      >
                        {nom}
                      </Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip
                          label={poste}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            bgcolor: theme.accentSoft,
                            color: theme.accent,
                            border: `1px solid ${theme.accentBorder}`,
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                        <Typography sx={{ color: theme.text.muted, fontSize: 11, fontWeight: 500 }}>
                          {taille} cm
                        </Typography>
                        <Typography sx={{ color: theme.text.muted, fontSize: 11 }}>•</Typography>
                        <Typography sx={{ color: theme.text.muted, fontSize: 11, fontWeight: 500 }}>
                          {getDecimalAge(date_naissance)} {t('canteraPage.years')}
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>

                  {/* Indicateurs daily */}
                  {hasDaily && (
                    <Box sx={{ mt: 'auto' }}>
                      <Typography sx={{ color: theme.text.muted, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.75 }}>
                        {t('canteraPage.individualStatus')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {dailyMetrics.map((m) => {
                          const v = metricsByKey[m.key];
                          if (v === undefined || v === null) return null;
                          return (
                            <Box
                              key={m.key}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.25,
                                px: 0.75,
                                py: 0.25,
                                borderRadius: `${theme.radius.sm}px`,
                                bgcolor: theme.withAlpha(m.color, 0.14),
                                border: `1px solid ${theme.withAlpha(m.color, 0.3)}`,
                              }}
                            >
                              <Typography sx={{ color: m.color, fontSize: 11, fontWeight: 700 }}>
                                {m.shortLabel}: {v}/10
                              </Typography>
                              {getTrendIcon(v, prevByKey[m.key], m.inverse)}
                            </Box>
                          );
                        })}
                      </Box>
                      {pain_zone && (
                        <Typography sx={{ color: theme.actionColors.missed, fontSize: 11, fontStyle: 'italic', mt: 0.75 }}>
                          {t('canteraPage.painZone')}: {pain_zone}
                        </Typography>
                      )}
                      {comment && (
                        <Typography sx={{ color: theme.text.secondary, fontSize: 11, fontStyle: 'italic', mt: 0.5 }}>
                          {t('canteraPage.comment')}: {comment}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Link>

              {/* Expansion badges + email */}
              {hasBadges && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'center', borderTop: `1px solid ${theme.surfaces.border}`, py: 0.25 }}>
                    <IconButton
                      onClick={(e) => handleExpandClick(player_id, e)}
                      size="small"
                      sx={{
                        color: theme.text.secondary,
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: theme.motion.fast,
                      }}
                    >
                      <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 2, pb: 2, pt: 1 }}>
                      {/* Email */}
                      <Box
                        sx={{
                          mb: 1.5, p: 1.25,
                          bgcolor: theme.surfaces.base,
                          border: `1px solid ${theme.surfaces.border}`,
                          borderRadius: `${theme.radius.md}px`,
                        }}
                      >
                        <Typography sx={{ color: theme.text.muted, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.75 }}>
                          {t('canteraPage.formAccess')}
                        </Typography>
                        <CanteraTextField
                          fullWidth
                          size="small"
                          type="email"
                          placeholder="correo@ejemplo.com"
                          value={playerEmails[player_id] || ''}
                          onChange={(e) => handleEmailChange(player_id, e.target.value)}
                          disabled={emailLoading[player_id]}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon sx={{ color: theme.text.muted, fontSize: 18 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ mb: 1 }}
                        />
                        <Stack direction="row" spacing={1}>
                          <CanteraButton
                            variant="secondary"
                            size="sm"
                            fullWidth
                            startIcon={<SaveIcon fontSize="small" />}
                            onClick={() => handleSaveEmail(player_id)}
                            disabled={emailLoading[player_id]}
                          >
                            {t('canteraPage.save')}
                          </CanteraButton>
                          <CanteraButton
                            variant="primary"
                            size="sm"
                            fullWidth
                            startIcon={<SendIcon fontSize="small" />}
                            onClick={() => handleSendAccess(player_id)}
                            disabled={emailLoading[player_id] || !playerEmails[player_id]}
                          >
                            {t('canteraPage.sendAccess')}
                          </CanteraButton>
                        </Stack>
                      </Box>

                      {/* Badges activité */}
                      <Typography sx={{ color: theme.text.muted, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.75 }}>
                        {t('canteraPage.playerInfo')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {badgeConfigs({
                          count_big_board, count_follow_list, count_informe, count_intel,
                          count_fiche_technique, count_video, count_medical, count_medidas,
                        }).filter((b) => b.show).map((b, i) => (
                          <Chip
                            key={i}
                            icon={b.icon}
                            label={b.count > 1 ? `${b.label} (${b.count})` : b.label}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: 10,
                              fontWeight: 600,
                              bgcolor: theme.withAlpha(b.color, 0.16),
                              color: b.color,
                              border: `1px solid ${theme.withAlpha(b.color, 0.35)}`,
                              '& .MuiChip-icon': { color: b.color },
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Collapse>
                </>
              )}
            </CanteraCard>
          </Grid>
        );
      })}
    </Grid>
  );

  const pageBg = `radial-gradient(circle at 15% 5%, ${theme.accentSoft} 0%, transparent 40%), radial-gradient(circle at 85% 95%, ${theme.withAlpha(theme.accentSecondary, 0.1)} 0%, transparent 40%), ${theme.surfaces.base}`;

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <CircularProgress size={28} sx={{ color: theme.accent }} />
        <Typography sx={{ color: theme.text.secondary, fontSize: 15, fontWeight: 500 }}>
          {t('canteraPage.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: pageBg, p: { xs: 1.5, md: 3 } }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: `${theme.radius.md}px`,
            fontWeight: 500,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {group && (
        <Box
          sx={{
            mb: 3,
            bgcolor: theme.surfaces.elevated,
            background: `linear-gradient(135deg, ${theme.accentSoft} 0%, ${theme.surfaces.elevated} 60%)`,
            border: `1px solid ${theme.surfaces.border}`,
            borderRadius: `${theme.radius.xl}px`,
            boxShadow: theme.shadows.md,
            overflow: 'hidden',
          }}
        >
          <Collapse in={showHeader} timeout="auto" unmountOnExit>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                p: { xs: 2, md: 3 },
                pb: 1.5,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    color: theme.text.primary,
                    fontSize: { xs: 20, sm: 26, md: 30 },
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.15,
                  }}
                >
                  {group.libelle}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: { xs: 120, md: 180, lg: 220 },
                  flexShrink: 0,
                  borderRadius: `${theme.radius.lg}px`,
                  overflow: 'hidden',
                  border: `1px solid ${theme.surfaces.border}`,
                  boxShadow: theme.shadows.sm,
                }}
              >
                <img
                  src={getTeamPhotoUrl(groupeId)}
                  alt={`Equipo ${group.libelle}`}
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </Box>
            </Box>
          </Collapse>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 1, md: 2 }, borderTop: showHeader ? `1px solid ${theme.surfaces.border}` : 'none' }}>
            <Box sx={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                TabIndicatorProps={{ sx: { bgcolor: theme.accent, height: 3, borderRadius: '3px 3px 0 0' } }}
                sx={{
                  minHeight: 44,
                  '& .MuiTab-root': {
                    minHeight: 44,
                    px: 1.5,
                    color: theme.text.secondary,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: 14,
                    letterSpacing: '0.01em',
                    '&.Mui-selected': { color: theme.accent },
                  },
                  '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 },
                }}
              >
                <Tab label={t('canteraPage.tabs.roster')} value="plantilla" />
                <Tab label={t('canteraPage.tabs.gameCenterStats')} value="gamecenter" />
                <Tab label={t('canteraPage.tabs.gameCenterVideo')} value="gamecentervideo" />
                <Tab label={t('canteraPage.tabs.statistics')} value="estadisticas" />
              </Tabs>
            </Box>
            <IconButton
              onClick={() => setShowHeader(!showHeader)}
              size="small"
              sx={{
                color: theme.text.secondary,
                transform: showHeader ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.motion.fast,
                ml: 0.5,
              }}
              aria-label="toggle-header"
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      {currentTab === 'plantilla' && (
        <>
          {renderTeamAverage()}
          {renderPlayers()}
        </>
      )}

      {currentTab === 'gamecenter' && <GameCenterEstadisticas groupeId={groupeId} />}

      {currentTab === 'gamecentervideo' && <GameCenterVideo groupeId={groupeId} />}

      {currentTab === 'estadisticas' && (
        isLegacyStatsUIEnabled() ? (
          <EstadisticasNew groupeId={groupeId} groupName={group ? group.libelle : 'Nom inconnu'} />
        ) : (
          <StatsRouterV2 groupeId={groupeId} groupName={group ? group.libelle : 'Nom inconnu'} />
        )
      )}
    </Box>
  );
};

export default Cantera;
